import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { parseOptionalHandicap, hasHandicap, eventHandicap, handicapLabel } from '../utils/handicap';
import { toast } from "sonner";
import { setActiveRoundForYardage, clearActiveRound } from "./RonRoHelpButton";
import { 
  Flag,
  Trophy,
  TrendingUp,
  Calendar as CalendarIcon,
  Trash2,
  ChevronRight,
  ChevronDown,
  Camera,
  Upload,
  X,
  Check,
  Users,
  User,
  Search,
  FileText,
  AlertTriangle,
  MoreHorizontal,
  Eye,
  Play,
  Settings,
  ArrowLeft,
  Edit,
  Clock,
  Mail,
  Pencil,
  UserPlus,
  MapPin,
  Circle,
  MessageCircle,
  PlusCircle,
  Send,
  RefreshCw,
  History
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Textarea } from "./ui/textarea";
import { format } from "date-fns";
import ScoreEntryForm from "./ScoreEntryForm";
import { CourseLookupModal, CourseParDisplay } from "./score-entry";
import { AddPlayersModal, CreateTeamModal } from "../App";
import UserBubble from "./UserBubble";
import { JoinTournamentButton, TournamentPrizeInfo } from "./JoinTournamentButton";
import UserProfileModal from "./UserProfileModal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Helper function to calculate strokes received on each hole for Match Play
// In match play, strokes are given based on difference between two players
const getMatchPlayStrokesOnHole = (playerHandicap, opponentHandicap, holeIdx, courseHandicap) => {
  if (!hasHandicap(playerHandicap) || !hasHandicap(opponentHandicap)) return 0;
  if (!courseHandicap || courseHandicap.length < 9 || playerHandicap === null || playerHandicap === undefined) {
    return 0;
  }
  
  // In match play, we compare individual handicaps
  const strokeDiff = Math.floor(playerHandicap - opponentHandicap);
  
  // If this player has higher handicap, they receive strokes
  if (strokeDiff <= 0) return 0;
  
  // Course handicap indicates difficulty of each hole (1 = hardest)
  const holeDifficulty = courseHandicap[holeIdx];
  
  // Player gets a stroke on holes where difficulty <= stroke difference
  if (holeDifficulty <= strokeDiff) {
    return 1;
  }
  return 0;
};

// Helper function for Skins-style multi-player stroke calculation
const getSkinsStrokesOnHole = (playerHandicap, minHandicap, holeIdx, holeHandicaps) => {
  if (!holeHandicaps || holeIdx >= holeHandicaps.length || playerHandicap === null || playerHandicap === undefined) {
    return 0;
  }
  
  const strokesReceived = Math.floor(playerHandicap - minHandicap);
  if (strokesReceived <= 0) return 0;
  
  const holeHcpRating = holeHandicaps[holeIdx];
  
  if (holeHcpRating <= strokesReceived) {
    return 1;
  }
  if (strokesReceived > 18 && holeHcpRating <= (strokesReceived - 18)) {
    return 2;
  }
  return 0;
};

const MatchPlayEventDetail = ({ eventId, onBack, user }) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [leaderboard, setLeaderboard] = useState([]);
  const [individualStandings, setIndividualStandings] = useState([]);
  const [standingsView, setStandingsView] = useState('team'); // 'team' or 'individual'
  const [handicaps, setHandicaps] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [weekData, setWeekData] = useState(null);
  
  // Scores tab states
  const [scoresWeek, setScoresWeek] = useState(1);
  const [weekScores, setWeekScores] = useState([]);
  const [weekHolePars, setWeekHolePars] = useState([]);
  const [weekTotalPar, setWeekTotalPar] = useState(null);
  const [holesType, setHolesType] = useState('front'); // 'front' for 1-9, 'back' for 10-18
  const [courseHandicap, setCourseHandicap] = useState([]); // Stroke index per hole [1-18]
  const [showScorecardView, setShowScorecardView] = useState(true); // Default to showing scorecard
  const [editingHolePars, setEditingHolePars] = useState(false);
  const [holeParsInput, setHoleParsInput] = useState(Array(9).fill(''));
  const [holesTypeInput, setHolesTypeInput] = useState('front');
  const [courseHandicapInput, setCourseHandicapInput] = useState([1, 3, 5, 7, 9, 11, 13, 15, 17]); // Default odd numbers for front 9
  const [savingHolePars, setSavingHolePars] = useState(false);
  const [scanningCoursePhoto, setScanningCoursePhoto] = useState(false);
  const [pendingScores, setPendingScores] = useState([]);
  const [selectedPlayerForScore, setSelectedPlayerForScore] = useState(null);
  const [scoreEntryData, setScoreEntryData] = useState({ holeScores: Array(9).fill(''), totalScore: '' });
  const [submittingScore, setSubmittingScore] = useState(false);
  const [scoreEntryMode, setScoreEntryMode] = useState('total'); // 'total', 'manual', or 'photo'
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [photoScanResult, setPhotoScanResult] = useState(null);
  const photoInputRef = useRef(null);
  const [userPendingScores, setUserPendingScores] = useState([]);
  const [showScoreHistory, setShowScoreHistory] = useState(null); // player_id or null
  const [scoreHistory, setScoreHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showSubstituteModal, setShowSubstituteModal] = useState(null); // {player_id, player_name, team_id} or null
  const [substituteType, setSubstituteType] = useState('existing'); // 'existing' or 'new'
  const [newSubName, setNewSubName] = useState('');
  const [selectedSubstitute, setSelectedSubstitute] = useState('');
  const [availableSubstitutes, setAvailableSubstitutes] = useState([]);
  const [addingSubstitute, setAddingSubstitute] = useState(false);
  
  // Sync scores to history state
  const [syncingScores, setSyncingScores] = useState(false);
  const [scoresSynced, setScoresSynced] = useState(false);
  
  // Manual points entry states
  const [showManualPointsModal, setShowManualPointsModal] = useState(null); // {matchup info} or null
  const [manualPointsData, setManualPointsData] = useState({
    player1_hole_points: '',
    player2_hole_points: '',
    player1_match_points: '',
    player2_match_points: ''
  });
  const [savingManualPoints, setSavingManualPoints] = useState(false);
  
  // Modal states
  const [showAddPlayers, setShowAddPlayers] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showAddScore, setShowAddScore] = useState(false);
  const [showSetMatchups, setShowSetMatchups] = useState(false);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [editingWeekMatchups, setEditingWeekMatchups] = useState(null);
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  
  // Email states
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [playerEmails, setPlayerEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailWeek, setEmailWeek] = useState(1);
  
  // Profile view state
  const [viewingProfileId, setViewingProfileId] = useState(null);
  
  // Join requests state (for organizers)
  const [joinRequests, setJoinRequests] = useState([]);
  const [loadingJoinRequests, setLoadingJoinRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Edit player handicap state (NEW)
  const [editingHandicapPlayer, setEditingHandicapPlayer] = useState(null);
  const [newHandicapValue, setNewHandicapValue] = useState('');

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}`);
      setEvent(response.data);
      
      // Set active round for RonRo yardage mode when game is in progress
      if (response.data.status === 'active' && response.data.course_id) {
        setActiveRoundForYardage({
          course_id: response.data.course_id,
          course_name: response.data.course_name,
          game_type: 'match_play',
          event_id: eventId,
          current_hole: 1
        });
      } else if (response.data.status === 'completed') {
        clearActiveRound();
      }
    } catch (error) {
      toast.error("Failed to load event");
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
    }
  };

  const fetchIndividualStandings = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/individual-standings`);
      setIndividualStandings(response.data.standings || []);
    } catch (error) {
    }
  };

  const fetchHandicaps = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/handicaps`);
      setHandicaps(response.data.handicaps || []);
    } catch (error) {
    }
  };

  // Fetch join requests for this event (organizer only)
  const fetchJoinRequests = async () => {
    if (!event || event.creator_id !== user?.user_id) return;
    setLoadingJoinRequests(true);
    try {
      const response = await axios.get(`${API}/leagues/join-requests`);
      // Filter requests for this specific event
      const eventRequests = (response.data.requests || []).filter(r => r.event_id === eventId);
      setJoinRequests(eventRequests);
    } catch (error) {
    } finally {
      setLoadingJoinRequests(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await axios.post(`${API}/leagues/join-requests/${requestId}/approve`);
      toast.success('Player added to league!');
      setJoinRequests(prev => prev.filter(r => r.request_id !== requestId));
      fetchEvent(); // Refresh event data to show new player
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await axios.post(`${API}/leagues/join-requests/${requestId}/reject`);
      toast.success('Request rejected');
      setJoinRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Update player handicap (NEW)
  const updatePlayerHandicap = async (playerId, handicap) => {
    try {
      await axios.patch(`${API}/match-play/events/${eventId}/players/${playerId}`, {
        handicap: parseOptionalHandicap(handicap)
      });
      toast.success("Handicap updated");
      setEditingHandicapPlayer(null);
      setNewHandicapValue('');
      fetchEvent();
    } catch (error) {
      toast.error("Failed to update handicap");
    }
  };

  const fetchWeekData = async (week) => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/weeks/${week}`);
      setWeekData(response.data);
    } catch (error) {
    }
  };

  const fetchWeekScores = async (week) => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/weeks/${week}/scores`);
      setWeekScores(response.data.scores || []);
      setWeekHolePars(response.data.hole_pars || []);
      setWeekTotalPar(response.data.total_par || null);
      setHolesType(response.data.holes_type || 'front');
      setCourseHandicap(response.data.course_handicap || []);
      // Initialize inputs if data exists
      if (response.data.hole_pars?.length === 9) {
        setHoleParsInput(response.data.hole_pars);
        setHolesTypeInput(response.data.holes_type || 'front');
      }
      if (response.data.course_handicap?.length === 9) {
        setCourseHandicapInput(response.data.course_handicap);
      }
    } catch (error) {
      setWeekScores([]);
      setWeekHolePars([]);
      setWeekTotalPar(null);
      setHolesType('front');
      setCourseHandicap([]);
    }
  };

  const saveHolePars = async () => {
    const pars = holeParsInput.map(Number);
    if (pars.length !== 9 || pars.some(p => !Number.isInteger(p) || p < 2 || p > 7)) {
      toast.error('Enter the real par for all nine holes');
      return;
    }
    setSavingHolePars(true);
    try {
      await axios.put(`${API}/match-play/events/${eventId}/hole-pars`, {
        hole_pars: pars,
        holes_type: holesTypeInput,
        course_handicap: courseHandicapInput.map(h => parseInt(h) || 1)
      });
      toast.success("Course settings saved!");
      setWeekHolePars(pars);
      setWeekTotalPar(pars.reduce((sum, p) => sum + p, 0));
      setHolesType(holesTypeInput);
      setCourseHandicap(courseHandicapInput.map(h => parseInt(h) || 1));
      setEditingHolePars(false);
    } catch (error) {
      toast.error("Failed to save course settings");
    } finally {
      setSavingHolePars(false);
    }
  };

  // Helper function to get hole number based on holes type
  const getHoleNumber = (index) => {
    return holesType === 'back' ? index + 10 : index + 1;
  };

  // Photo scan for course settings (Par and Stroke Index)
  const handleCoursePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setScanningCoursePhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scan_type', 'course_settings'); // Tell backend to extract par and stroke index
      
      const response = await axios.post(`${API}/scan-scorecard`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        // Extract par values if available
        if (response.data.hole_pars && response.data.hole_pars.length === 9) {
          setHoleParsInput(response.data.hole_pars);
          toast.success('Par values extracted from photo!');
        }
        
        // Extract stroke index if available
        if (response.data.stroke_index && response.data.stroke_index.length === 9) {
          setCourseHandicapInput(response.data.stroke_index);
          toast.success('Stroke index extracted from photo!');
        }
        
        // If we got hole scores (from a played round), show them too
        if (response.data.hole_scores && !response.data.hole_pars) {
          toast.info('Photo shows player scores. Use "Scan Photo" in score entry to capture those.');
        }
        
        if (!response.data.hole_pars && !response.data.stroke_index) {
          toast.warning('Could not extract course settings. Please enter manually.');
        }
      } else {
        toast.error(response.data.error || 'Failed to scan photo');
      }
    } catch (error) {
      toast.error('Failed to scan photo. Please enter manually.');
    } finally {
      setScanningCoursePhoto(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Save manual points for a match
  const saveManualPoints = async () => {
    if (!showManualPointsModal) return;
    
    const { week_number, player1_id, player2_id } = showManualPointsModal;
    
    // Validate points
    const p1_hole = parseFloat(manualPointsData.player1_hole_points) || 0;
    const p2_hole = parseFloat(manualPointsData.player2_hole_points) || 0;
    const p1_match = parseFloat(manualPointsData.player1_match_points) || 0;
    const p2_match = parseFloat(manualPointsData.player2_match_points) || 0;
    
    // Hole points must add up to exactly 9 (1 point per hole)
    if (p1_hole + p2_hole !== 9) {
      toast.error('Hole points must total exactly 9 (1 point per hole: win=1, tie=0.5 each)');
      return;
    }
    
    // Match points should be 5/0 or 2.5/2.5
    if ((p1_match + p2_match) !== 5) {
      toast.error('Match points must total 5 (5/0, 2.5/2.5, or 0/5)');
      return;
    }
    
    setSavingManualPoints(true);
    try {
      await axios.post(`${API}/match-play/events/${eventId}/manual-points`, {
        week_number,
        player1_id,
        player2_id,
        player1_hole_points: p1_hole,
        player2_hole_points: p2_hole,
        player1_match_points: p1_match,
        player2_match_points: p2_match
      });
      
      toast.success('Points saved successfully!');
      setShowManualPointsModal(null);
      setManualPointsData({
        player1_hole_points: '',
        player2_hole_points: '',
        player1_match_points: '',
        player2_match_points: ''
      });
      
      // Refresh week scores
      fetchWeekScores(scoresWeek);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save points');
    } finally {
      setSavingManualPoints(false);
    }
  };

  const fetchPendingScores = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/pending-scores`);
      setPendingScores(response.data.pending_rounds || []);
    } catch (error) {
      setPendingScores([]);
    }
  };

  const fetchUserPendingScores = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/my-pending-scores`);
      setUserPendingScores(response.data.pending_rounds || []);
    } catch (error) {
      setUserPendingScores([]);
    }
  };

  // Photo scanner handlers
  const handlePhotoSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setPhotoFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(selectedFile);
      setPhotoScanResult(null);
    }
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setPhotoFile(droppedFile);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(droppedFile);
      setPhotoScanResult(null);
    }
  };

  const analyzeScorecard = async () => {
    if (!photoFile) {
      toast.error("Please select a scorecard image first");
      return;
    }

    setAnalyzingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);

      const response = await axios.post(`${API}/scorecard/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setPhotoScanResult(response.data);
        // Extract 9 hole scores (front 9 or first 9)
        const holes = response.data.holes || [];
        const scores = holes.slice(0, 9).map(h => h.score?.toString() || '');
        // Pad with empty strings if less than 9 holes
        while (scores.length < 9) scores.push('');
        setScoreEntryData({ ...scoreEntryData, holeScores: scores });
        toast.success("Scorecard scanned! Review the scores below.");
      } else {
        toast.error(response.data.message || "Could not analyze scorecard");
      }
    } catch (error) {
      toast.error("Failed to analyze scorecard");
    } finally {
      setAnalyzingPhoto(false);
    }
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoScanResult(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const fetchScoreHistory = async (playerId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/score-history/${playerId}`);
      setScoreHistory(response.data);
      setShowScoreHistory(playerId);
    } catch (error) {
      toast.error("Failed to load score history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const openSubstituteModal = (player) => {
    setShowSubstituteModal({
      player_id: player.player_id,
      player_name: player.name,
      team_id: player.team_id,
      team_name: player.team_name
    });
    setSubstituteType('existing');
    setNewSubName('');
    setSelectedSubstitute('');
    
    // Get available substitutes (players not in teams or marked as substitutes)
    const allPlayers = event.player_details || [];
    const teamPlayerIds = new Set();
    event.teams?.forEach(t => t.player_ids?.forEach(pid => teamPlayerIds.add(pid)));
    
    // Filter to substitutes or unassigned players
    const subs = allPlayers.filter(p => 
      p.is_substitute || !teamPlayerIds.has(p.player_id)
    );
    setAvailableSubstitutes(subs);
  };

  const handleSubstitutePlayer = async () => {
    if (substituteType === 'new' && !newSubName.trim()) {
      toast.error("Please enter substitute name");
      return;
    }
    if (substituteType === 'existing' && !selectedSubstitute) {
      toast.error("Please select a substitute");
      return;
    }

    setAddingSubstitute(true);
    try {
      let subPlayerId = selectedSubstitute;
      
      // If new substitute, create them first
      if (substituteType === 'new') {
        const addResponse = await axios.post(`${API}/match-play/events/${eventId}/players`, {
          names: [newSubName.trim()],
          is_substitute: true
        });
        if (addResponse.data.added?.length > 0) {
          subPlayerId = addResponse.data.added[0].player_id;
        } else {
          throw new Error("Failed to create substitute");
        }
      }
      
      // Record the substitution for this week
      await axios.post(`${API}/match-play/events/${eventId}/weeks/${scoresWeek}/substitute`, {
        original_player_id: showSubstituteModal.player_id,
        substitute_player_id: subPlayerId,
        team_id: showSubstituteModal.team_id
      });
      
      toast.success(`Substituted ${showSubstituteModal.player_name} for Week ${scoresWeek}`);
      setShowSubstituteModal(null);
      fetchWeekScores(scoresWeek);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to substitute player");
    } finally {
      setAddingSubstitute(false);
    }
  };

  useEffect(() => {
    fetchEvent();
    fetchLeaderboard();
    fetchIndividualStandings();
    fetchHandicaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]); // Fetch functions are stable and use eventId internally

  // Fetch join requests when event loads and user is organizer
  useEffect(() => {
    if (event && event.creator_id === user?.user_id) {
      fetchJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.creator_id, user?.user_id]); // Only re-run when creator or user changes

  useEffect(() => {
    if (activeTab === 'schedule') {
      fetchWeekData(selectedWeek);
    }
    if (activeTab === 'scores') {
      fetchWeekScores(scoresWeek);
      fetchPendingScores();
      fetchUserPendingScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedWeek, scoresWeek]); // Fetch functions are stable

  const handleStartEvent = async () => {
    try {
      await axios.post(`${API}/match-play/events/${eventId}/start`);
      toast.success("League started!");
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start league");
    }
  };

  const refreshAll = () => {
    fetchEvent();
    fetchLeaderboard();
    fetchIndividualStandings();
    fetchHandicaps();
    if (activeTab === 'schedule') {
      fetchWeekData(selectedWeek);
    }
  };

  // Sync scores from completed games to Recent Rounds/History
  const syncScoresToHistory = async () => {
    setSyncingScores(true);
    try {
      const response = await axios.post(`${API}/match-play/events/${eventId}/sync-scores`);
      if (response.data.scores_synced > 0) {
        toast.success(`Synced ${response.data.scores_synced} score(s) to your Recent Rounds!`);
        setScoresSynced(true);
      } else {
        toast.info("Scores already synced or no linked players with scorecards");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync scores");
    } finally {
      setSyncingScores(false);
    }
  };

  // Handle course selection from lookup modal
  const handleCourseSelect = async (courseData) => {
    try {
      await axios.patch(`${API}/match-play/events/${eventId}`, {
        course_name: courseData.name,
        course_id: courseData.course_id,
        hole_pars: courseData.hole_pars || [],
        hole_handicaps: courseData.hole_handicaps || []
      });
      toast.success(`Course set to ${courseData.name}`);
      fetchEvent();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  // Handle delete league
  const handleDeleteLeague = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/match-play/events/${eventId}`);
      toast.success("League deleted successfully");
      onBack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete league');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
      </div>
    );
  }

  if (!event) return null;

  const isCreator = event.is_creator;
  const isDraft = event.status === 'draft';

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Player Profile Modal */}
      {viewingProfileId && (
        <UserProfileModal 
          userId={viewingProfileId} 
          onClose={() => setViewingProfileId(null)} 
        />
      )}
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={onBack} className="p-2" data-testid="match-event-back" aria-label="Back to games">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-black text-[var(--ps-text-primary)]">{event.name}</h1>
          <p className="text-sm text-[var(--ps-text-secondary)] flex items-center flex-wrap gap-1">
            <button 
              onClick={() => setShowCourseLookup(true)}
              className="text-green-700 hover:text-green-800 hover:underline flex items-center gap-1 font-medium"
              title="Click to change course"
            >
              <MapPin className="w-3 h-3" />
              {event.course_name || 'Select Course'}
            </button>
            <span>•</span> {event.teams?.length || 0} teams <span>•</span> {event.total_weeks} weeks
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
              event.status === 'active' ? 'bg-green-100 text-green-700' :
              event.status === 'completed' ? 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]' :
              'bg-amber-100 text-amber-700'
            }`}>
              {event.status === 'active' ? 'Active' : event.status === 'completed' ? 'Completed' : 'Setup'}
            </span>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isCreator && isDraft && (
            <span 
              onClick={event.teams?.length < 2 ? undefined : handleStartEvent} 
              className={`text-sm font-bold cursor-pointer flex items-center gap-1 ${
                event.teams?.length < 2 
                  ? 'text-[var(--ps-text-muted)] cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <Play className="w-4 h-4" />
              Start League
            </span>
          )}
          {isCreator && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2" data-testid="league-settings-btn">
                  <MoreHorizontal className="w-5 h-5 text-[var(--ps-text-secondary)]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  data-testid="delete-league-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete League
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete League
              </CardTitle>
              <CardDescription>
                This action cannot be undone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-[var(--ps-text-primary)] mb-4">
                Are you sure you want to delete <strong>{event.name}</strong>?
              </p>
              <p className="text-sm text-[var(--ps-text-muted)]">
                This will permanently delete the league, including all players, teams, matchups, and scores.
              </p>
            </CardContent>
            <div className="px-6 pb-6 flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteConfirm(false)} 
                className="flex-1"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteLeague} 
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <div className="spinner w-5 h-5 border-white border-t-transparent" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete League
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Course Lookup Modal */}
      <CourseLookupModal
        isOpen={showCourseLookup}
        onClose={() => setShowCourseLookup(false)}
        onCourseSelect={handleCourseSelect}
        currentCourseName={event.course_name}
        title="Find Course for Match Play"
      />

      {/* Course Par Display */}
      {event.course_name && event.hole_pars && event.hole_pars.length > 0 && (
        <CourseParDisplay
          selectedCourseData={{
            name: event.course_name,
            location: event.course_location,
            par: event.hole_pars.reduce((sum, p) => sum + (parseInt(p) || 0), 0),
            hole_pars: event.hole_pars,
            hole_handicaps: event.hole_handicaps || []
          }}
          numberOfHoles={9}
          startingNine="front"
          setStartingNine={() => {}}
          getActualHoleNumber={(idx) => idx + 1}
        />
      )}

      {/* Tournament Entry Fee - Join Button for Non-Players */}
      {event.entry_fee > 0 && !isCreator && isDraft && 
       !(event.players || []).includes(user?.user_id) &&
       !(event.guest_players || []).some(p => p.linked_user_id === user?.user_id) && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <TournamentPrizeInfo 
              entryFee={event.entry_fee} 
              playerCount={(event.players || []).length + (event.guest_players || []).length}
              compact={true}
            />
            <JoinTournamentButton
              tournamentId={eventId}
              tournamentType="match_play"
              entryFee={event.entry_fee}
              tournamentName={event.name}
              onJoined={() => refreshAll()}
              data-testid="join-match-play-btn"
            />
          </div>
        </div>
      )}

      {/* Setup Banner for Draft */}
      {isDraft && isCreator && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-900 mb-2">League Setup Checklist</h3>
            <div className="grid gap-2 md:grid-cols-2">
              <div className={`flex items-center gap-2 ${(event.player_details?.length || 0) >= 4 ? 'text-green-700' : 'text-amber-700'}`}>
                {(event.player_details?.length || 0) >= 4 ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                <span className="text-sm">Add players ({event.player_details?.length || 0}/4 min)</span>
              </div>
              <div className={`flex items-center gap-2 ${event.teams?.length >= 2 ? 'text-green-700' : 'text-amber-700'}`}>
                {event.teams?.length >= 2 ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                <span className="text-sm">Create teams ({event.teams?.length || 0}/2 min)</span>
              </div>
              <div className={`flex items-center gap-2 ${event.weekly_matchups?.length > 0 ? 'text-green-700' : 'text-amber-700'}`}>
                {event.weekly_matchups?.length > 0 ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                <span className="text-sm">Set up matchups</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* HERO LEADERBOARD - Primary view when entering league */}
      {event.status === 'active' && (
        <div className="mb-6 space-y-4">
          {/* League Stats Banner */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 text-white text-center">
              <p className="text-2xl font-bold">{Math.min(selectedWeek, event.total_weeks || 12)}/{event.total_weeks || 12}</p>
              <p className="text-xs opacity-80">Weeks Played</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white text-center">
              <p className="text-2xl font-bold">{(event.teams || []).length}</p>
              <p className="text-xs opacity-80">Teams</p>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 text-white text-center">
              <p className="text-2xl font-bold">{(event.player_details || []).length}</p>
              <p className="text-xs opacity-80">Players</p>
            </div>
          </div>

          {/* Top 3 Podium */}
          {leaderboard.length > 0 && (
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 p-3">
                <h3 className="text-white font-bold text-center flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  LEAGUE STANDINGS
                </h3>
              </div>
              <CardContent className="p-4">
                {/* Podium Display */}
                <div className="flex items-end justify-center gap-2 mb-4 h-40">
                  {/* 2nd Place */}
                  {leaderboard[1]?.position != null && (
                    <div 
                      className="flex-1 max-w-[100px] cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => {
                        const playerId = leaderboard[1].player_ids?.[0];
                        if (playerId && !playerId.startsWith('gp_')) setViewingProfileId(playerId);
                      }}
                    >
                      <div className="bg-gradient-to-b from-gray-300 to-gray-400 rounded-t-lg p-3 h-24 flex flex-col items-center justify-end">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-gray-600 shadow-md mb-1">
                          2
                        </div>
                        <p className="text-xs font-semibold text-gray-800 truncate w-full text-center">{leaderboard[1].team_name}</p>
                        <p className="text-lg font-bold text-gray-900">{leaderboard[1].total_points}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* 1st Place */}
                  {leaderboard[0]?.position != null && (
                    <div 
                      className="flex-1 max-w-[120px] cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => {
                        const playerId = leaderboard[0].player_ids?.[0];
                        if (playerId && !playerId.startsWith('gp_')) setViewingProfileId(playerId);
                      }}
                    >
                      <div className="bg-gradient-to-b from-amber-300 to-amber-500 rounded-t-lg p-3 h-32 flex flex-col items-center justify-end relative">
                        <div className="absolute -top-3 text-2xl">👑</div>
                        <div className="w-12 h-12 bg-amber-400 border-4 border-white rounded-full flex items-center justify-center font-bold text-white shadow-lg mb-1">
                          1
                        </div>
                        <p className="text-sm font-bold text-amber-900 truncate w-full text-center">{leaderboard[0].team_name}</p>
                        <p className="text-xl font-bold text-amber-900">{leaderboard[0].total_points}</p>
                        <p className="text-xs text-amber-800">{leaderboard[0].wins}W-{leaderboard[0].losses}L</p>
                      </div>
                    </div>
                  )}
                  
                  {/* 3rd Place */}
                  {leaderboard[2]?.position != null && (
                    <div 
                      className="flex-1 max-w-[100px] cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => {
                        const playerId = leaderboard[2].player_ids?.[0];
                        if (playerId && !playerId.startsWith('gp_')) setViewingProfileId(playerId);
                      }}
                    >
                      <div className="bg-gradient-to-b from-orange-300 to-orange-400 rounded-t-lg p-3 h-20 flex flex-col items-center justify-end">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-orange-600 shadow-md mb-1">
                          3
                        </div>
                        <p className="text-xs font-semibold text-orange-800 truncate w-full text-center">{leaderboard[2].team_name}</p>
                        <p className="text-lg font-bold text-orange-900">{leaderboard[2].total_points}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Your Position Highlight */}
                {(() => {
                  const myTeam = leaderboard.find(t => 
                    t.player_ids?.includes(user?.user_id) || 
                    (event.guest_players || []).some(g => g.linked_user_id === user?.user_id && t.player_ids?.includes(g.player_id))
                  );
                  const leader = leaderboard[0];
                  if (myTeam && myTeam.position > 3) {
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white">
                              {myTeam.position}
                            </div>
                            <div>
                              <p className="font-semibold text-blue-900">Your Team: {myTeam.team_name}</p>
                              <p className="text-sm text-blue-700">{myTeam.wins}W-{myTeam.losses}L-{myTeam.ties}T</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-700">{myTeam.total_points}</p>
                            {leader && myTeam.position > 1 && (
                              <p className="text-xs text-blue-600">{(leader.total_points - myTeam.total_points).toFixed(1)} pts behind</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Full Standings - Scrollable */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[var(--ps-text-secondary)]">Full Standings</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setStandingsView('team')}
                        className={`px-2 py-1 text-xs rounded ${standingsView === 'team' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                      >
                        Teams
                      </button>
                      <button
                        onClick={() => setStandingsView('individual')}
                        className={`px-2 py-1 text-xs rounded ${standingsView === 'individual' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                      >
                        Individual
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {standingsView === 'team' ? (
                      leaderboard.filter(team => team.position == null || team.position > 3).map((team) => (
                        <div 
                          key={team.team_id} 
                          className="flex items-center gap-3 p-2 bg-[var(--ps-bg-surface)] rounded-lg hover:bg-[var(--ps-bg-hover)] cursor-pointer transition-colors"
                          onClick={() => {
                            const playerId = team.player_ids?.[0];
                            if (playerId && !playerId.startsWith('gp_')) setViewingProfileId(playerId);
                          }}
                        >
                          <div className="w-8 h-8 bg-[var(--ps-bg-elevated)] rounded-full flex items-center justify-center font-semibold text-sm text-[var(--ps-text-secondary)]">
                            {team.position ?? 'N/A'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[var(--ps-text-primary)] truncate">{team.team_name}</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">{team.players?.map(p => p.name).join(' & ')}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">{team.total_points}</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">{team.wins}W-{team.losses}L</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      individualStandings.map((player) => (
                        <div 
                          key={player.player_id} 
                          className="flex items-center gap-3 p-2 bg-[var(--ps-bg-surface)] rounded-lg hover:bg-[var(--ps-bg-hover)] cursor-pointer transition-colors"
                          onClick={() => {
                            if (player.player_id && !player.player_id.startsWith('gp_')) setViewingProfileId(player.player_id);
                          }}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                            player.position != null && player.position <= 3
                              ? player.position === 1 ? 'bg-amber-400 text-white' 
                                : player.position === 2 ? 'bg-gray-400 text-white' 
                                : 'bg-orange-400 text-white'
                              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]'
                          }`}>
                            {player.position ?? 'N/A'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[var(--ps-text-primary)] truncate">{player.name}</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">{player.team_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">{player.total_points?.toFixed(1)}</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">{player.wins}W-{player.losses}L</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No standings yet message */}
          {leaderboard.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                <h3 className="font-semibold text-[var(--ps-text-primary)] mb-1">No Results Yet</h3>
                <p className="text-sm text-[var(--ps-text-muted)]">Complete your first match to see the leaderboard!</p>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => setActiveTab('scores')}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 py-6"
            >
              <Play className="w-5 h-5" />
              Enter Scores
            </Button>
            <Button
              variant="outline"
              onClick={() => setActiveTab('schedule')}
              className="flex items-center justify-center gap-2 py-6"
            >
              <CalendarIcon className="w-5 h-5" />
              View Schedule
            </Button>
          </div>
        </div>
      )}

      {/* Tabs - Add Scores tab when league is active, Email tab for organizers */}
      <div className="flex flex-wrap gap-4 mb-6 border-b border-[var(--ps-border)] pb-4">
        {[...(event.status === 'active' ? ['scores'] : []), 'leaderboard', 'players', 'teams', 'handicaps', 'schedule', ...(isCreator ? ['requests', 'email'] : [])].map((tab, index) => (
          <span
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`text-sm font-bold cursor-pointer transition-colors capitalize ${
              activeTab === tab 
                ? index % 2 === 0 
                  ? 'text-red-600 underline underline-offset-4' 
                  : 'text-blue-600 underline underline-offset-4'
                : index % 2 === 0
                  ? 'text-red-400 hover:text-red-600'
                  : 'text-blue-400 hover:text-blue-600'
            }`}
          >
            {tab === 'scores' ? 'Add Scores' : tab === 'email' ? 'Email Players' : tab === 'requests' ? `Join Requests${joinRequests.length > 0 ? ` (${joinRequests.length})` : ''}` : tab}
          </span>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'leaderboard' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Standings
              </CardTitle>
              {/* Toggle between Team and Individual */}
              <div className="flex bg-[var(--ps-bg-elevated)] rounded-lg p-1">
                <button
                  onClick={() => setStandingsView('team')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    standingsView === 'team' 
                      ? 'bg-[var(--ps-bg-surface)] text-green-700 shadow-sm' 
                      : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                  }`}
                  data-testid="standings-team-toggle"
                >
                  Teams
                </button>
                <button
                  onClick={() => setStandingsView('individual')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    standingsView === 'individual' 
                      ? 'bg-[var(--ps-bg-surface)] text-green-700 shadow-sm' 
                      : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                  }`}
                  data-testid="standings-individual-toggle"
                >
                  Players
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Team Standings */}
            {standingsView === 'team' && (
              <>
                {leaderboard.length === 0 ? (
                  <p className="text-center text-[var(--ps-text-secondary)] py-8">No results yet. Start playing to see the leaderboard!</p>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((team, idx) => (
                      <div key={team.team_id} className={`flex items-center gap-4 p-4 rounded-lg ${
                        idx === 0 ? 'bg-amber-50 border border-amber-200' :
                        idx === 1 ? 'bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)]' :
                        idx === 2 ? 'bg-orange-50 border border-orange-200' : 'bg-[var(--ps-bg-surface)] border'
                      }`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-amber-400 text-white' :
                          idx === 1 ? 'bg-[var(--ps-text-muted)] text-[var(--ps-bg-surface)]' :
                          idx === 2 ? 'bg-orange-400 text-white' : 'bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]'
                        }`}>
                          {team.position ?? 'N/A'}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[var(--ps-text-primary)]">{team.team_name}</h4>
                          <p className="text-sm text-[var(--ps-text-muted)]">
                            {team.players?.map(p => p.name).join(' & ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-700">{team.total_points}</p>
                          <p className="text-xs text-[var(--ps-text-muted)]">{team.wins}W-{team.losses}L-{team.ties}T</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Individual Player Standings */}
            {standingsView === 'individual' && (
              <>
                {individualStandings.length === 0 ? (
                  <p className="text-center text-[var(--ps-text-secondary)] py-8">No individual results yet. Complete some matches to see player standings!</p>
                ) : (
                  <div className="space-y-2">
                    {/* Header row */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-[var(--ps-text-muted)] uppercase tracking-wider border-b">
                      <div className="col-span-1">#</div>
                      <div className="col-span-4">Player</div>
                      <div className="col-span-2 text-center">Points</div>
                      <div className="col-span-2 text-center">Record</div>
                      <div className="col-span-3 text-center">Avg Score</div>
                    </div>
                    
                    {individualStandings.map((player, idx) => (
                      <div 
                        key={player.player_id} 
                        data-testid={`player-standing-${idx}`}
                        className={`grid grid-cols-12 gap-2 items-center p-3 rounded-lg ${
                          idx === 0 ? 'bg-amber-50 border border-amber-200' :
                          idx === 1 ? 'bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)]' :
                          idx === 2 ? 'bg-orange-50 border border-orange-200' : 'bg-[var(--ps-bg-surface)] border'
                        }`}
                      >
                        {/* Position */}
                        <div className="col-span-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            idx === 0 ? 'bg-amber-400 text-white' :
                            idx === 1 ? 'bg-[var(--ps-text-muted)] text-[var(--ps-bg-surface)]' :
                            idx === 2 ? 'bg-orange-400 text-white' : 'bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]'
                          }`}>
                            {player.position ?? 'N/A'}
                          </div>
                        </div>
                        
                        {/* Player Info */}
                        <div className="col-span-4">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[var(--ps-text-primary)]">{player.name}</span>
                            {player.is_guest && (
                              <span className="px-1.5 py-0.5 text-xs bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)] rounded">Guest</span>
                            )}
                          </div>
                          <p className="text-xs text-[var(--ps-text-muted)] truncate">{player.team_name}</p>
                        </div>
                        
                        {/* Total Points */}
                        <div className="col-span-2 text-center">
                          <p className="text-xl font-bold text-green-700">{player.total_points.toFixed(1)}</p>
                          <p className="text-xs text-[var(--ps-text-muted)]">
                            {player.hole_points.toFixed(1)}H + {player.match_points.toFixed(1)}M
                          </p>
                        </div>
                        
                        {/* W-L-T Record */}
                        <div className="col-span-2 text-center">
                          <p className="text-sm font-medium text-[var(--ps-text-primary)]">
                            {player.wins}-{player.losses}-{player.ties}
                          </p>
                          <p className="text-xs text-[var(--ps-text-muted)]">{player.matches_played} played</p>
                        </div>
                        
                        {/* Avg Score */}
                        <div className="col-span-3 text-center">
                          {player.avg_score > 0 ? (
                            <>
                              <p className="text-sm font-medium text-[var(--ps-text-primary)]">{player.avg_score}</p>
                              <p className="text-xs text-[var(--ps-text-muted)]">strokes/round</p>
                            </>
                          ) : (
                            <p className="text-xs text-[var(--ps-text-muted)]">No rounds</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Sync Scores to Recent Rounds - Only show for completed weeks */}
            {event.players?.some(p => p.weekly_scores?.length > 0) && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">Add to Recent Rounds</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Sync your match play scores to Recent Rounds so they count toward your handicap calculation.
                    </p>
                    <Button
                      onClick={syncScoresToHistory}
                      disabled={syncingScores || scoresSynced}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                      data-testid="sync-scores-btn"
                    >
                      {syncingScores ? (
                        <div className="spinner w-4 h-4 border-white border-t-transparent mr-2" />
                      ) : scoresSynced ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {scoresSynced ? 'Scores Synced!' : 'Sync to My Rounds'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Scores Tab - Simplified Add Score Interface */}
      {activeTab === 'scores' && (
        <div className="space-y-4">
          {/* Week Selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {Array.from({ length: event.total_weeks }, (_, i) => i + 1).map((week) => (
              <Button
                key={week}
                variant={scoresWeek === week ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setScoresWeek(week);
                  setSelectedPlayerForScore(null);
                  setScoreEntryData({ holeScores: Array(9).fill(''), totalScore: '' });
                }}
                className={scoresWeek === week ? 'btn-primary' : ''}
                data-testid={`scores-week-${week}`}
              >
                Week {week}
              </Button>
            ))}
          </div>

          {/* Front 9 / Back 9 Selector - Visible to All */}
          <div className="flex items-center justify-between bg-[var(--ps-bg-elevated)] rounded-lg p-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--ps-text-secondary)] font-medium">Playing:</span>
              <div className="flex rounded-lg border overflow-hidden bg-[var(--ps-bg-surface)]">
                <button
                  onClick={async () => {
                    if (isCreator || event.admins?.includes(user?.id)) {
                      try {
                        await axios.put(`${API}/match-play/events/${eventId}/hole-pars`, {
                          hole_pars: weekHolePars.length === 9 ? weekHolePars : [],
                          holes_type: 'front'
                        });
                        setHolesType('front');
                        toast.success("Set to Front 9");
                      } catch (error) {
                        toast.error("Failed to update");
                      }
                    }
                  }}
                  disabled={!(isCreator || event.admins?.includes(user?.id))}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    holesType === 'front' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                  } ${!(isCreator || event.admins?.includes(user?.id)) ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  Front 9 (1-9)
                </button>
                <button
                  onClick={async () => {
                    if (isCreator || event.admins?.includes(user?.id)) {
                      try {
                        await axios.put(`${API}/match-play/events/${eventId}/hole-pars`, {
                          hole_pars: weekHolePars.length === 9 ? weekHolePars : [],
                          holes_type: 'back'
                        });
                        setHolesType('back');
                        toast.success("Set to Back 9");
                      } catch (error) {
                        toast.error("Failed to update");
                      }
                    }
                  }}
                  disabled={!(isCreator || event.admins?.includes(user?.id))}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    holesType === 'back' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                  } ${!(isCreator || event.admins?.includes(user?.id)) ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  Back 9 (10-18)
                </button>
              </div>
            </div>
            {weekTotalPar && (
              <div className="text-sm text-[var(--ps-text-secondary)]">
                Par: <span className="font-bold text-green-700">{weekTotalPar}</span>
              </div>
            )}
          </div>

          {/* User's Pending Scores (Shows to all users who submitted scores) */}
          {userPendingScores.length > 0 && !(isCreator || event.admins?.includes(user?.id)) && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                  <Clock className="w-4 h-4" />
                  Your Scores Pending Review ({userPendingScores.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {userPendingScores.map((round) => (
                  <div key={round.round_id} className="flex items-center justify-between p-3 bg-[var(--ps-bg-surface)] rounded-lg border">
                    <div>
                      <p className="font-medium">{round.player_name}</p>
                      <p className="text-sm text-[var(--ps-text-muted)]">
                        Week {round.week_number} • Total: {round.total_score}
                      </p>
                      {round.hole_scores?.length === 9 && (
                        <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                          Holes: {round.hole_scores.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                        Awaiting Review
                      </span>
                      <p className="text-xs text-[var(--ps-text-muted)] mt-1">{round.date}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Organizer/Admin Pending Scores Review */}
          {(isCreator || event.admins?.includes(user?.id)) && pendingScores.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <Clock className="w-4 h-4" />
                  Scores Pending Your Review ({pendingScores.length})
                </CardTitle>
                <p className="text-xs text-amber-600">Review and approve player-submitted scores</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingScores.map((round) => (
                  <div key={round.round_id} className="p-3 bg-[var(--ps-bg-surface)] rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{round.player_name}</p>
                        <p className="text-sm text-[var(--ps-text-muted)]">
                          Week {round.week_number} • {round.date}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-green-700">{round.total_score}</p>
                    </div>
                    {round.hole_scores?.length === 9 && (
                      <div className="mb-3 p-2 bg-[var(--ps-bg-elevated)] rounded">
                        <p className="text-xs text-[var(--ps-text-muted)] mb-1">Hole-by-Hole Scores ({holesType === 'back' ? 'Back 9' : 'Front 9'}):</p>
                        <div className="grid grid-cols-9 gap-1 text-center">
                          {round.hole_scores.map((score, idx) => (
                            <div key={`round-hole-${idx + 1}`}>
                              <div className="text-xs text-[var(--ps-text-muted)]">{getHoleNumber(idx)}</div>
                              <div className="font-medium text-sm">{score}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/match-play/events/${eventId}/rounds/${round.round_id}/review`, { action: 'reject' });
                            toast.success("Score rejected");
                            fetchPendingScores();
                            fetchWeekScores(scoresWeek);
                          } catch (err) {
                            toast.error("Failed to reject");
                          }
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/match-play/events/${eventId}/rounds/${round.round_id}/review`, { action: 'approve' });
                            toast.success("Score approved!");
                            fetchPendingScores();
                            fetchWeekScores(scoresWeek);
                            refreshAll();
                          } catch (err) {
                            toast.error("Failed to approve");
                          }
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Full Scorecard View - Vertical Format */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Week {scoresWeek} Scorecard
                </CardTitle>
                <Button
                  variant={showScorecardView ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowScorecardView(!showScorecardView)}
                  className={showScorecardView ? 'btn-primary' : ''}
                >
                  {showScorecardView ? 'Hide Scores' : 'View All Scores'}
                </Button>
              </div>
            </CardHeader>
            
            {showScorecardView && (
              <CardContent className="space-y-4">
                {/* Hole Pars Setup - Organizers Only */}
                {(isCreator || event.admins?.includes(user?.id)) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-green-800 text-sm">Course Settings</h4>
                        {weekHolePars.length === 9 && !editingHolePars && (
                          <>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              {holesType === 'back' ? 'Back 9 (10-18)' : 'Front 9 (1-9)'}
                            </span>
                            {courseHandicap.length === 9 && (
                              <span className="flex items-center gap-1 text-xs text-green-600">
                                <Check className="w-3 h-3" /> Saved
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {!editingHolePars ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingHolePars(true);
                            setHolesTypeInput(holesType);
                            if (courseHandicap.length === 9) {
                              setCourseHandicapInput(courseHandicap);
                            }
                          }}
                          className="h-7 text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          {weekHolePars.length === 9 && courseHandicap.length === 9 ? 'Edit Course' : 
                           weekHolePars.length === 9 ? 'Add Stroke Index' : 'Setup Course'}
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => setEditingHolePars(false)} className="h-7 text-xs">Cancel</Button>
                          <Button size="sm" className="btn-primary h-7 text-xs" onClick={saveHolePars} disabled={savingHolePars}>
                            {savingHolePars ? '...' : 'Save'}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {editingHolePars ? (
                      <div className="space-y-3">
                        {/* Front/Back 9 Selector */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[var(--ps-text-secondary)]">Holes:</span>
                          <div className="flex rounded-lg border overflow-hidden">
                            <button
                              onClick={() => setHolesTypeInput('front')}
                              className={`px-3 py-1 text-xs font-medium transition-colors ${
                                holesTypeInput === 'front' 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                              }`}
                            >
                              Front 9 (1-9)
                            </button>
                            <button
                              onClick={() => setHolesTypeInput('back')}
                              className={`px-3 py-1 text-xs font-medium transition-colors ${
                                holesTypeInput === 'back' 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                              }`}
                            >
                              Back 9 (10-18)
                            </button>
                          </div>
                        </div>
                        {/* Par Input Grid */}
                        <div>
                          <div className="text-xs text-[var(--ps-text-muted)] mb-1">Par per hole:</div>
                          <div className="grid grid-cols-10 gap-1">
                            {holeParsInput.map((par, idx) => (
                              <div key={`par-input-hole-${idx + 1}`} className="text-center">
                                <div className="text-xs text-[var(--ps-text-muted)] mb-1">{holesTypeInput === 'back' ? idx + 10 : idx + 1}</div>
                                <select
                                  value={par}
                                  onChange={(e) => {
                                    const newPars = [...holeParsInput];
                                    newPars[idx] = parseInt(e.target.value);
                                    setHoleParsInput(newPars);
                                  }}
                                  className="w-full p-1 border rounded text-center bg-[var(--ps-bg-surface)] text-sm"
                                >
                                  <option value={3}>3</option>
                                  <option value={4}>4</option>
                                  <option value={5}>5</option>
                                  <option value={6}>6</option>
                                </select>
                              </div>
                            ))}
                            <div className="text-center">
                              <div className="text-xs text-[var(--ps-text-muted)] mb-1">Tot</div>
                              <div className="p-1 bg-green-100 rounded font-bold text-green-800 text-sm">
                                {holeParsInput.reduce((sum, p) => sum + (parseInt(p) || 4), 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Course Handicap (Stroke Index) Input */}
                        <div>
                          <div className="text-xs text-[var(--ps-text-muted)] mb-1">Stroke Index (1=hardest, 18=easiest):</div>
                          <div className="grid grid-cols-10 gap-1">
                            {courseHandicapInput.map((hcp, idx) => (
                              <div key={`hole-${idx}`} className="text-center">
                                <div className="text-xs text-[var(--ps-text-muted)] mb-1">{holesTypeInput === 'back' ? idx + 10 : idx + 1}</div>
                                <select
                                  value={hcp}
                                  onChange={(e) => {
                                    const newHcp = [...courseHandicapInput];
                                    newHcp[idx] = parseInt(e.target.value);
                                    setCourseHandicapInput(newHcp);
                                  }}
                                  className="w-full p-1 border rounded text-center bg-[var(--ps-bg-surface)] text-sm"
                                >
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                            <div className="text-center">
                              <div className="text-xs text-[var(--ps-text-muted)] mb-1">-</div>
                              <div className="p-1 bg-[var(--ps-bg-elevated)] rounded text-[var(--ps-text-muted)] text-sm">-</div>
                            </div>
                          </div>
                        </div>

                        {/* Photo Scan Option */}
                        <div className="border-t pt-3">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-[var(--ps-text-muted)]">Or scan from scorecard photo:</div>
                            <div className="flex gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleCoursePhotoScan}
                                className="hidden"
                                id="course-photo-input"
                              />
                              <label
                                htmlFor="course-photo-input"
                                className="cursor-pointer px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                              >
                                <Camera className="w-3 h-3" />
                                {scanningCoursePhoto ? 'Scanning...' : 'Scan Photo'}
                              </label>
                            </div>
                          </div>
                          {scanningCoursePhoto && (
                            <div className="mt-2 text-xs text-blue-600 animate-pulse">
                              Analyzing scorecard image...
                            </div>
                          )}
                        </div>
                      </div>
                    ) : weekHolePars.length === 9 ? (
                      <div className="space-y-2">
                        {/* Par Display */}
                        <div className="grid grid-cols-10 gap-1">
                          {weekHolePars.map((par, idx) => (
                            <div key={`score-${idx}`} className="text-center bg-white rounded p-1 border">
                              <div className="text-xs text-[var(--ps-text-muted)]">{getHoleNumber(idx)}</div>
                              <div className="font-bold text-green-700 text-sm">{par}</div>
                            </div>
                          ))}
                          <div className="text-center bg-green-100 rounded p-1">
                            <div className="text-xs text-green-600">Tot</div>
                            <div className="font-bold text-green-800 text-sm">{weekTotalPar}</div>
                          </div>
                        </div>
                        {/* Course Handicap Display */}
                        {courseHandicap.length === 9 && (
                          <div className="grid grid-cols-10 gap-1">
                            {courseHandicap.map((hcp, idx) => (
                              <div key={`par-${idx}`} className="text-center bg-amber-50 rounded p-1 border border-amber-200">
                                <div className="text-xs text-amber-600">SI</div>
                                <div className="font-bold text-amber-700 text-sm">{hcp}</div>
                              </div>
                            ))}
                            <div className="text-center bg-amber-100 rounded p-1">
                              <div className="text-xs text-amber-600">Idx</div>
                              <div className="font-bold text-amber-700 text-sm">-</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600">No course settings configured. Click "Setup Course" to add par and stroke index.</p>
                    )}
                  </div>
                )}

                {/* Course Info Display - For Non-Organizers */}
                {!(isCreator || event.admins?.includes(user?.id)) && (weekHolePars.length === 9 || courseHandicap.length === 9) && (
                  <div className="bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg p-3">
                    <h4 className="font-medium text-[var(--ps-text-primary)] text-sm mb-2">
                      Course Info 
                      <span className="text-xs bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] px-2 py-0.5 rounded ml-2">
                        {holesType === 'back' ? 'Back 9 (10-18)' : 'Front 9 (1-9)'}
                      </span>
                    </h4>
                    <div className="space-y-2">
                      {weekHolePars.length === 9 && (
                        <div className="grid grid-cols-10 gap-1">
                          {weekHolePars.map((par, idx) => (
                            <div key={`score-${idx}`} className="text-center bg-white rounded p-1 border">
                              <div className="text-xs text-[var(--ps-text-muted)]">{getHoleNumber(idx)}</div>
                              <div className="font-bold text-green-700 text-sm">{par}</div>
                            </div>
                          ))}
                          <div className="text-center bg-green-100 rounded p-1">
                            <div className="text-xs text-green-600">Tot</div>
                            <div className="font-bold text-green-800 text-sm">{weekTotalPar}</div>
                          </div>
                        </div>
                      )}
                      {courseHandicap.length === 9 && (
                        <div className="grid grid-cols-10 gap-1">
                          {courseHandicap.map((hcp, idx) => (
                            <div key={`par-${idx}`} className="text-center bg-amber-50 rounded p-1 border border-amber-200">
                              <div className="text-xs text-amber-600">SI</div>
                              <div className="font-bold text-amber-700 text-sm">{hcp}</div>
                            </div>
                          ))}
                          <div className="text-center bg-amber-100 rounded p-1">
                            <div className="text-xs text-amber-600">Idx</div>
                            <div className="font-bold text-amber-700 text-sm">-</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Player Scorecards - Vertical List Format */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {weekScores.filter(p => p.has_score).length === 0 ? (
                    <p className="text-center text-[var(--ps-text-muted)] py-6">No scores recorded for Week {scoresWeek} yet.</p>
                  ) : (
                    weekScores.filter(p => p.has_score).map((player) => {
                      const totalScore = player.score?.total_score || 0;
                      const toPar = weekTotalPar ? totalScore - weekTotalPar : null;
                      const hasHoleScores = player.score?.hole_scores?.length === 9;
                      
                      return (
                        <div key={player.player_id} className="border rounded-lg overflow-hidden">
                          {/* Player Header */}
                          <div className="bg-[var(--ps-bg-elevated)] p-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <UserBubble
                                userId={player.user_id}
                                picture={player.picture}
                                name={player.name}
                                bubbleColor="green"
                                size="sm"
                                clickable={!!player.user_id}
                              />
                              <div>
                                <div className="font-medium text-sm flex items-center gap-1">
                                  {player.name}
                                  {player.is_substitute && <span className="px-1 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Sub</span>}
                                </div>
                                <div data-testid={`match-week-handicap-${player.player_id}`} className="text-xs text-[var(--ps-text-muted)]">{player.team_name} • HCP: {handicapLabel(player.handicap)}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-green-800">{totalScore}</div>
                              <div className={`text-sm font-medium ${
                                toPar === null ? 'text-[var(--ps-text-muted)]' :
                                toPar < 0 ? 'text-red-600' : 
                                toPar === 0 ? 'text-[var(--ps-text-secondary)]' : 
                                'text-blue-600'
                              }`}>
                                {toPar !== null ? (toPar > 0 ? `+${toPar}` : toPar === 0 ? 'E' : toPar) : '-'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Hole-by-Hole Scores Grid */}
                          {hasHoleScores ? (() => {
                            // Find opponent for this player to show strokes
                            const playerTeam = event.teams?.find(t => 
                              t.player_ids?.includes(player.player_id) || 
                              t.guest_player_ids?.includes(player.player_id)
                            );
                            const weekMatchups = event.weekly_matchups?.find(w => w.week === scoresWeek);
                            let opponentHandicap = null;
                            if (playerTeam && weekMatchups) {
                              const match = weekMatchups.matches?.find(m => 
                                m.team1_id === playerTeam.team_id || m.team2_id === playerTeam.team_id
                              );
                              if (match) {
                                const opponentTeamId = match.team1_id === playerTeam.team_id ? match.team2_id : match.team1_id;
                                const opponentTeam = event.teams?.find(t => t.team_id === opponentTeamId);
                                const playerIndex = [...(playerTeam.player_ids || []), ...(playerTeam.guest_player_ids || [])].indexOf(player.player_id);
                                const opponentIds = [...(opponentTeam?.player_ids || []), ...(opponentTeam?.guest_player_ids || [])];
                                const opponentId = opponentIds[playerIndex];
                                const opponent = weekScores.find(p => p.player_id === opponentId);
                                opponentHandicap = eventHandicap(opponent);
                              }
                            }
                            const playerInitial = player.name?.charAt(0).toUpperCase() || '?';
                            
                            return (
                            <div className="p-2">
                              <div className="grid grid-cols-10 gap-1 text-center text-xs">
                                {/* Hole Numbers with stroke indicators */}
                                <div className="col-span-10 grid grid-cols-10 gap-1 mb-1">
                                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((idx) => {
                                    const strokesOnHole = getMatchPlayStrokesOnHole(player.handicap, opponentHandicap, idx, courseHandicap);
                                    return (
                                      <div key={`header-${idx}`} className="text-[var(--ps-text-muted)] font-medium">
                                        <div>{getHoleNumber(idx)}</div>
                                        {strokesOnHole > 0 && (
                                          <div className="text-[9px] text-blue-600 font-bold leading-none">
                                            -{strokesOnHole} {playerInitial}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  <div className="text-green-600 font-medium">Tot</div>
                                </div>
                                {/* Par Row */}
                                {weekHolePars.length === 9 && (
                                  <div className="col-span-10 grid grid-cols-10 gap-1 mb-1">
                                    {weekHolePars.map((par, idx) => (
                                      <div key={`par-row-${idx}`} className="text-[var(--ps-text-muted)]">P{par}</div>
                                    ))}
                                    <div className="text-green-600">P{weekTotalPar}</div>
                                  </div>
                                )}
                                {/* Score Row */}
                                <div className="col-span-10 grid grid-cols-10 gap-1">
                                  {player.score.hole_scores.map((score, idx) => {
                                    const par = weekHolePars[idx];
                                    const diff = par ? score - par : 0;
                                    let cellClass = 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] border';
                                    if (par) {
                                      if (diff <= -2) cellClass = 'bg-yellow-200 text-yellow-800 font-bold';
                                      else if (diff === -1) cellClass = 'bg-red-100 text-red-700 font-medium';
                                      else if (diff === 0) cellClass = 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] border';
                                      else if (diff === 1) cellClass = 'bg-blue-100 text-blue-700';
                                      else cellClass = 'bg-blue-200 text-blue-800';
                                    }
                                    return (
                                      <div key={`cell-${idx}`} className={`p-1 rounded ${cellClass}`}>
                                        {score}
                                      </div>
                                    );
                                  })}
                                  <div className="p-1 rounded bg-green-100 text-green-800 font-bold">
                                    {totalScore}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                          })() : (
                            <div className="p-2 text-center">
                              <p className="text-xs text-[var(--ps-text-muted)] mb-2">Total score only - no hole-by-hole breakdown</p>
                              {(isCreator || event.admins?.includes(user?.id)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Find the opponent for this player in this week's matchups
                                    const weekMatchups = event.weekly_matchups?.find(w => w.week === scoresWeek);
                                    if (weekMatchups) {
                                      // Find the player's team
                                      const playerTeam = event.teams?.find(t => 
                                        t.player_ids?.includes(player.player_id) || 
                                        t.guest_player_ids?.includes(player.player_id)
                                      );
                                      if (playerTeam) {
                                        // Find the match for this team
                                        const match = weekMatchups.matches?.find(m => 
                                          m.team1_id === playerTeam.team_id || m.team2_id === playerTeam.team_id
                                        );
                                        if (match) {
                                          // Find opponent team
                                          const opponentTeamId = match.team1_id === playerTeam.team_id ? match.team2_id : match.team1_id;
                                          const opponentTeam = event.teams?.find(t => t.team_id === opponentTeamId);
                                          // Find the corresponding opponent player (same position)
                                          const playerIndex = [...(playerTeam.player_ids || []), ...(playerTeam.guest_player_ids || [])].indexOf(player.player_id);
                                          const opponentIds = [...(opponentTeam?.player_ids || []), ...(opponentTeam?.guest_player_ids || [])];
                                          const opponentId = opponentIds[playerIndex];
                                          const opponent = weekScores.find(p => p.player_id === opponentId);
                                          
                                          if (opponent) {
                                            setShowManualPointsModal({
                                              week_number: scoresWeek,
                                              player1_id: player.player_id,
                                              player1_name: player.name,
                                              player2_id: opponent.player_id,
                                              player2_name: opponent.name
                                            });
                                            setManualPointsData({
                                              player1_hole_points: '',
                                              player2_hole_points: '',
                                              player1_match_points: '',
                                              player2_match_points: ''
                                            });
                                          } else {
                                            toast.error('Could not find opponent for this match');
                                          }
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Enter Match Points
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Legend */}
                {weekHolePars.length === 9 && weekScores.filter(p => p.has_score && p.score?.hole_scores?.length === 9).length > 0 && (
                  <div className="flex flex-wrap gap-3 text-xs text-[var(--ps-text-muted)] pt-2 border-t">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-200 rounded"></div>Eagle+</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-100 rounded"></div>Birdie</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-white border rounded"></div>Par</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-100 rounded"></div>Bogey</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-200 rounded"></div>Double+</div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Player Scores List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-green-600" />
                Week {scoresWeek} Scores
              </CardTitle>
              <p className="text-sm text-[var(--ps-text-muted)]">Click a player to add/edit their score</p>
            </CardHeader>
            <CardContent>
              {weekScores.length === 0 ? (
                <p className="text-center text-[var(--ps-text-muted)] py-8">No players in teams yet. Add players and create teams first.</p>
              ) : (
                <div className="space-y-2">
                  {weekScores.map((player) => (
                    <div key={player.player_id}>
                      {/* Player Row */}
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedPlayerForScore === player.player_id
                            ? 'bg-green-50 border-green-300'
                            : player.has_score
                            ? 'bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)]'
                            : 'bg-[var(--ps-bg-surface)] hover:bg-[var(--ps-bg-elevated)]'
                        } ${!isCreator && player.user_id !== user?.user_id ? 'cursor-default' : 'cursor-pointer'}`}
                        onClick={() => {
                          // Only allow clicking if creator OR this is the current user's own row
                          const canEdit = isCreator || (player.user_id && player.user_id === user?.user_id);
                          if (!canEdit) return;
                          
                          if (selectedPlayerForScore === player.player_id) {
                            setSelectedPlayerForScore(null);
                            setScoreEntryData({ holeScores: Array(9).fill(''), totalScore: '' });
                          } else {
                            setSelectedPlayerForScore(player.player_id);
                            if (player.score?.hole_scores?.length === 9) {
                              setScoreEntryData({
                                holeScores: player.score.hole_scores.map(String),
                                totalScore: player.score.total_score?.toString() || ''
                              });
                            } else if (player.score?.total_score) {
                              setScoreEntryData({
                                holeScores: Array(9).fill(''),
                                totalScore: player.score.total_score.toString()
                              });
                            } else {
                              setScoreEntryData({ holeScores: Array(9).fill(''), totalScore: '' });
                            }
                          }
                        }}
                        data-testid={`player-score-row-${player.player_id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-green-700" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{player.name}</span>
                              {player.is_guest && (
                                <span className="px-1.5 py-0.5 text-xs bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)] rounded">Guest</span>
                              )}
                              {player.is_substitute && (
                                <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Sub</span>
                              )}
                              {player.is_weekly_sub && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Week Sub</span>
                              )}
                            </div>
                            <p data-testid={`match-entry-handicap-${player.player_id}`} className="text-xs text-[var(--ps-text-muted)]">{player.team_name} • HCP: {handicapLabel(player.handicap)}</p>
                            {/* Hole-by-Hole Scores inline display with stroke indicators */}
                            {player.has_score && player.score?.hole_scores?.length === 9 && (() => {
                              // Find opponent for this player to show strokes
                              const playerTeam = event.teams?.find(t => 
                                t.player_ids?.includes(player.player_id) || 
                                t.guest_player_ids?.includes(player.player_id)
                              );
                              const weekMatchups = event.weekly_matchups?.find(w => w.week === scoresWeek);
                              let opponentHandicap = null;
                              if (playerTeam && weekMatchups) {
                                const match = weekMatchups.matches?.find(m => 
                                  m.team1_id === playerTeam.team_id || m.team2_id === playerTeam.team_id
                                );
                                if (match) {
                                  const opponentTeamId = match.team1_id === playerTeam.team_id ? match.team2_id : match.team1_id;
                                  const opponentTeam = event.teams?.find(t => t.team_id === opponentTeamId);
                                  const playerIndex = [...(playerTeam.player_ids || []), ...(playerTeam.guest_player_ids || [])].indexOf(player.player_id);
                                  const opponentIds = [...(opponentTeam?.player_ids || []), ...(opponentTeam?.guest_player_ids || [])];
                                  const opponentId = opponentIds[playerIndex];
                                  const opponent = weekScores.find(p => p.player_id === opponentId);
                                  opponentHandicap = eventHandicap(opponent);
                                }
                              }
                              const playerInitial = player.name?.charAt(0).toUpperCase() || '?';
                              
                              return (
                                <div className="flex gap-0.5 mt-1 flex-wrap items-end">
                                  {player.score.hole_scores.map((score, idx) => {
                                    const par = weekHolePars[idx];
                                    const diff = par ? score - par : 0;
                                    const strokesOnHole = getMatchPlayStrokesOnHole(player.handicap, opponentHandicap, idx, courseHandicap);
                                    let cellClass = 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] border';
                                    if (par) {
                                      if (diff <= -2) cellClass = 'bg-yellow-200 text-yellow-800';
                                      else if (diff === -1) cellClass = 'bg-red-100 text-red-700';
                                      else if (diff === 0) cellClass = 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] border';
                                      else if (diff === 1) cellClass = 'bg-blue-100 text-blue-700';
                                      else cellClass = 'bg-blue-200 text-blue-800';
                                    }
                                    return (
                                      <div key={`inline-${idx}`} className="flex flex-col items-center">
                                        {strokesOnHole > 0 && (
                                          <span className="text-[8px] text-blue-600 font-bold leading-none mb-0.5">
                                            -{strokesOnHole}{playerInitial}
                                          </span>
                                        )}
                                        <div className={`w-5 h-5 text-xs flex items-center justify-center rounded ${cellClass}`}>
                                          {score}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Substitute button - only for organizers */}
                          {(isCreator || event.admins?.includes(user?.id)) && !player.has_score && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSubstituteModal(player);
                              }}
                              title="Substitute player for this week"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Score & Status */}
                          <div className="text-right flex items-center gap-1">
                            {player.has_score ? (
                              <>
                                <div>
                                  <p className="text-xl font-bold text-green-700">{player.score.total_score}</p>
                                  {player.score.review_status === 'pending' && (
                                    <p className="text-xs text-amber-600">Pending</p>
                                  )}
                                </div>
                                {player.score.review_status === 'approved' && (
                                  <Check className="w-5 h-5 text-green-600" />
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-xl font-bold text-[var(--ps-text-muted)]">--</p>
                              </>
                            )}
                          </div>
                          {/* History button for organizers */}
                          {(isCreator || event.admins?.includes(user?.id)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchScoreHistory(player.player_id);
                              }}
                              title="View score history"
                            >
                              <History className="w-4 h-4 text-[var(--ps-text-muted)] hover:text-[var(--ps-text-secondary)]" />
                            </Button>
                          )}
                          {/* Chevron - only show for editable rows */}
                          {(isCreator || (player.user_id && player.user_id === user?.user_id)) ? (
                            <ChevronRight className={`w-5 h-5 text-[var(--ps-text-muted)] transition-transform ${
                              selectedPlayerForScore === player.player_id ? 'rotate-90' : ''
                            }`} />
                          ) : (
                            <span className="text-xs text-[var(--ps-text-muted)] italic">View only</span>
                          )}
                        </div>
                      </div>

                      {/* Expanded Score Entry - Using Unified ScoreEntryForm */}
                      {selectedPlayerForScore === player.player_id && (isCreator || (player.user_id && player.user_id === user?.user_id)) && (
                        <div className="mt-2 p-4 bg-green-50 rounded-lg border border-green-200 animate-fade-in">
                          <ScoreEntryForm
                            courseId={event.course_id}
                            startingNine={holesType}
                            getActualHoleNumber={(index) => holesType === 'back' ? index + 10 : index + 1}
                            eventHoles={9}
                            initialScores={player.score?.hole_scores?.length === 9 
                              ? player.score.hole_scores.map(String) 
                              : Array(9).fill('')}
                            onScoresChange={(newScores) => {
                              setScoreEntryData(prev => ({
                                ...prev,
                                holeScores: newScores.slice(0, 9)
                              }));
                            }}
                            playerName={player.name}
                            showHolesSelector={false}
                            showScanOption={true}
                            showVoiceOption={true}
                            autoSave={false}
                            submitButtonText="Save Score"
                            showTotalDisplay={true}
                            holePars={weekHolePars || []}
                            holeYardages={event?.hole_yardages || []}
                            showYardages={event?.hole_yardages && event?.hole_yardages.length > 0}
                            compact={true}
                            disabled={submittingScore}
                            onSave={async ({ scores, total }) => {
                              setSubmittingScore(true);
                              try {
                                const response = await axios.post(`${API}/match-play/events/${eventId}/rounds`, {
                                  player_id: player.player_id,
                                  week_number: scoresWeek,
                                  score_type: 'hole_by_hole',
                                  hole_scores: scores,
                                  date: new Date().toISOString().split('T')[0]
                                });
                                
                                if (response.data.review_status === 'pending') {
                                  toast.success("Score submitted for review");
                                  fetchUserPendingScores();
                                } else {
                                  toast.success("Score saved!");
                                }
                                
                                setSelectedPlayerForScore(null);
                                setScoreEntryData({ holeScores: Array(9).fill(''), totalScore: '' });
                                fetchWeekScores(scoresWeek);
                                fetchPendingScores();
                                refreshAll();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to save score");
                              } finally {
                                setSubmittingScore(false);
                              }
                            }}
                            onCancel={() => {
                              setSelectedPlayerForScore(null);
                              setScoreEntryData({ holeScores: Array(9).fill(''), totalScore: '' });
                            }}
                          />
                          
                          {/* Handicap Net Score Display */}
                          {hasHandicap(eventHandicap(player)) && (
                            <div className="mt-3 pt-3 border-t border-green-200 flex items-center gap-4">
                              <span data-testid={`match-net-preview-handicap-${player.player_id}`} className="text-sm text-[var(--ps-text-secondary)]">Handicap: {handicapLabel(eventHandicap(player))}</span>
                              {scoreEntryData.holeScores.some(s => s !== '') && (
                                <span className="text-sm text-[var(--ps-text-secondary)]">
                                  Net: <span className="font-semibold text-[var(--ps-text-primary)]">
                                    {(scoreEntryData.holeScores.reduce((sum, s) => sum + (parseInt(s) || 0), 0) - player.handicap).toFixed(1)}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Review notice for non-organizers */}
                          {!(isCreator || event.admins?.includes(user?.id)) && (
                            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded text-center mt-3">
                              Your score will be submitted for organizer review before being added to the leaderboard.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="space-y-4">
          {/* Week Selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {Array.from({ length: event.total_weeks }, (_, i) => i + 1).map((week) => (
              <Button
                key={week}
                variant={selectedWeek === week ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedWeek(week)}
                className={selectedWeek === week ? 'btn-primary' : ''}
              >
                Week {week}
              </Button>
            ))}
          </div>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle>Week {selectedWeek} Matchups</CardTitle>
              {isCreator && (
                <div className="flex flex-wrap gap-4">
                  <span 
                    onClick={() => setShowScheduleManager(true)}
                    className="text-sm font-bold text-red-600 hover:text-red-800 cursor-pointer flex items-center gap-1"
                  >
                    <CalendarIcon className="w-4 h-4" />
                    Manage Schedule
                  </span>
                  <span 
                    onClick={() => {
                      const weekMatchup = event.weekly_matchups?.find(m => m.week === selectedWeek);
                      setEditingWeekMatchups(weekMatchup?.matches || null);
                      setShowSetMatchups(true);
                    }}
                    className="text-sm font-bold text-blue-600 hover:text-blue-800 cursor-pointer flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    {weekData?.matches?.length > 0 ? 'Edit Week' : 'Set Week'}
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!weekData || weekData.matches?.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-3" />
                  <p className="text-[var(--ps-text-muted)]">No matchups set for this week</p>
                  {isCreator && (
                    <div className="flex flex-wrap gap-4 justify-center mt-4">
                      <span 
                        onClick={() => setShowScheduleManager(true)}
                        className="text-sm font-bold text-red-600 hover:text-red-800 cursor-pointer flex items-center gap-1"
                      >
                        <CalendarIcon className="w-4 h-4" />
                        Manage Full Schedule
                      </span>
                      <span 
                        onClick={() => {
                          setEditingWeekMatchups(null);
                          setShowSetMatchups(true);
                        }}
                        className="text-sm font-bold text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        Set This Week
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {weekData.matches.map((match) => (
                    <div key={match.result_id} className="border rounded-lg overflow-hidden">
                      {/* Team Header */}
                      <div className="flex items-center justify-between bg-[var(--ps-bg-elevated)] p-4">
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-800">{match.team1.name}</h4>
                        </div>
                        <div className="px-4 text-center">
                          <div className={`px-4 py-2 rounded-lg font-bold ${
                            match.status === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]'
                          }`}>
                            {match.status === 'completed' 
                              ? `${match.team1_total_points || 0} - ${match.team2_total_points || 0}` 
                              : 'vs'}
                          </div>
                          <span className="text-xs text-[var(--ps-text-muted)]">Team Points</span>
                        </div>
                        <div className="flex-1 text-right">
                          <h4 className="font-semibold text-green-800">{match.team2.name}</h4>
                        </div>
                      </div>
                      
                      {/* Player vs Player Matches */}
                      {match.player_matches && match.player_matches.length > 0 && (
                        <div className="divide-y">
                          {match.player_matches.map((pm, idx) => {
                            const p1Name = weekData.player_scores?.[pm.player1_id]?.player_name || 'Player 1';
                            const p2Name = weekData.player_scores?.[pm.player2_id]?.player_name || 'Player 2';
                            return (
                              <div key={`week-${idx}`} className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium">{p1Name}</p>
                                    {pm.status === 'completed' && (
                                      <div className="text-sm text-[var(--ps-text-muted)] mt-1">
                                        <span>Score: {pm.player1_total_score}</span>
                                        <span className="mx-2">•</span>
                                        <span>Holes: {pm.player1_hole_points}</span>
                                        <span className="mx-2">•</span>
                                        <span>Match: {pm.player1_match_points}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="px-4 text-center">
                                    {pm.status === 'completed' ? (
                                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                                        pm.player1_total_points > pm.player2_total_points 
                                          ? 'bg-green-100 text-green-700' 
                                          : pm.player2_total_points > pm.player1_total_points
                                            ? 'bg-red-50 text-red-600'
                                            : 'bg-amber-50 text-amber-700'
                                      }`}>
                                        {pm.player1_total_points} - {pm.player2_total_points}
                                      </div>
                                    ) : (
                                      <span className="text-[var(--ps-text-muted)]">vs</span>
                                    )}
                                    <div className="text-xs text-[var(--ps-text-muted)] mt-1">of 14 pts</div>
                                  </div>
                                  <div className="flex-1 text-right">
                                    <p className="font-medium">{p2Name}</p>
                                    {pm.status === 'completed' && (
                                      <div className="text-sm text-[var(--ps-text-muted)] mt-1">
                                        <span>Match: {pm.player2_match_points}</span>
                                        <span className="mx-2">•</span>
                                        <span>Holes: {pm.player2_hole_points}</span>
                                        <span className="mx-2">•</span>
                                        <span>Score: {pm.player2_total_score}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Pending indicator */}
                      {match.status === 'pending' && (
                        <div className="p-4 bg-amber-50 text-center text-sm text-amber-700">
                          Waiting for all players to submit scores
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Join Requests Tab - Only for organizers */}
      {activeTab === 'requests' && isCreator && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Join Requests
            </CardTitle>
            <CardDescription>
              Review and manage requests from players who want to join your league
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingJoinRequests ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6 border-blue-600 border-t-transparent" />
              </div>
            ) : joinRequests.length === 0 ? (
              <div className="text-center py-8 text-[var(--ps-text-secondary)]">
                <UserPlus className="w-12 h-12 mx-auto mb-3 text-[var(--ps-text-muted)]" />
                <p>No pending join requests</p>
                <p className="text-sm mt-1">When players request to join your league, they'll appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {joinRequests.map((request) => (
                  <div 
                    key={request.request_id}
                    className="flex items-center justify-between p-4 bg-[var(--ps-bg-elevated)] rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {request.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold text-[var(--ps-text-primary)]">{request.user_name}</p>
                        <p className="text-sm text-[var(--ps-text-secondary)]">{request.user_email}</p>
                        <p className="text-xs text-[var(--ps-text-muted)]">
                          Requested {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveRequest(request.request_id)}
                        disabled={processingRequest === request.request_id}
                        className="bg-green-600 hover:bg-blue-600 text-white"
                      >
                        {processingRequest === request.request_id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectRequest(request.request_id)}
                        disabled={processingRequest === request.request_id}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Tab - Only for organizers */}
      {activeTab === 'email' && isCreator && (
        <div className="space-y-6">
          {/* Send Master Scoresheet */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Send Master Scoresheet
              </CardTitle>
              <CardDescription>
                Send a professional scoresheet with standings and player scores to all league members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">Week (optional)</label>
                <Select value={String(emailWeek)} onValueChange={(v) => setEmailWeek(Number(v))}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All weeks" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="0">All Weeks</SelectItem>
                    {Array.from({ length: event.total_weeks }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>Week {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">Custom Message (optional)</label>
                <Textarea
                  placeholder="Add a personal message to include with the scoresheet..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                />
              </div>
              <Button 
                className="btn-primary"
                disabled={sendingEmail}
                onClick={async () => {
                  setSendingEmail(true);
                  try {
                    const response = await axios.post(`${API}/match-play/events/${eventId}/send-scoresheet`, {
                      week_number: emailWeek || null,
                      message: emailMessage
                    });
                    toast.success(response.data.message);
                    setEmailMessage('');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to send email');
                  } finally {
                    setSendingEmail(false);
                  }
                }}
              >
                {sendingEmail ? (
                  <div className="spinner w-5 h-5 border-white border-t-transparent" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Scoresheet to All Players
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Custom Announcement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Send Custom Announcement
              </CardTitle>
              <CardDescription>
                Send a custom email announcement to all league members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">Subject *</label>
                <Input
                  placeholder="Email subject line..."
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">Message *</label>
                <Textarea
                  placeholder="Write your announcement..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={5}
                />
              </div>
              <Button 
                className="btn-primary"
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                onClick={async () => {
                  setSendingEmail(true);
                  try {
                    const response = await axios.post(`${API}/match-play/events/${eventId}/send-email`, {
                      subject: emailSubject,
                      message: emailMessage,
                      include_scores: false,
                      include_standings: false
                    });
                    toast.success(response.data.message);
                    setEmailSubject('');
                    setEmailMessage('');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to send email');
                  } finally {
                    setSendingEmail(false);
                  }
                }}
              >
                {sendingEmail ? (
                  <div className="spinner w-5 h-5 border-white border-t-transparent" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Announcement
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Player Email List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[var(--ps-text-secondary)]" />
                Player Emails
              </CardTitle>
              <CardDescription>
                Manage email addresses for league communications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {event.player_details?.map((player) => (
                  <div key={player.player_id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 font-medium text-sm">
                          {player.name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{player.name}</p>
                        <p className="text-xs text-[var(--ps-text-muted)]">
                          {player.type === 'guest' ? 'Guest' : 'Registered'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {player.email ? (
                        <span className="text-sm text-[var(--ps-text-secondary)] flex items-center gap-1">
                          <Check className="w-4 h-4 text-green-500" />
                          {player.email}
                        </span>
                      ) : player.type === 'guest' ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="email"
                            placeholder="Add email..."
                            className="w-48 h-8 text-sm"
                            onBlur={async (e) => {
                              const email = e.target.value.trim();
                              if (email) {
                                try {
                                  await axios.put(`${API}/match-play/events/${eventId}/player-email/${player.player_id}`, { email });
                                  toast.success('Email saved');
                                  fetchEvent();
                                } catch (err) {
                                  toast.error('Failed to save email');
                                }
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600">No email on file</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Email count summary */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-[var(--ps-text-secondary)]">
                  <strong>{event.player_details?.filter(p => p.email).length || 0}</strong> of {' '}
                  <strong>{event.player_details?.length || 0}</strong> players have email addresses
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'teams' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Teams ({event.teams?.length || 0})</CardTitle>
            {isCreator && isDraft && (
              <span 
                onClick={() => setShowCreateTeam(true)}
                className="text-sm font-bold text-blue-600 hover:text-blue-800 cursor-pointer flex items-center gap-1"
              >
                <PlusCircle className="w-4 h-4" />
                Create Team
              </span>
            )}
          </CardHeader>
          <CardContent>
            {event.teams?.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-3" />
                <p className="text-[var(--ps-text-muted)]">No teams created yet</p>
                {isCreator && isDraft && (
                  <span 
                    onClick={() => setShowCreateTeam(true)}
                    className="mt-4 inline-block text-sm font-bold text-red-600 hover:text-red-800 cursor-pointer"
                  >
                    Create First Team
                  </span>
                )}
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {event.teams.map((team) => {
                  // Get players for this team - check both player_id and user_id for matching
                  const teamPlayers = team.player_ids.map(pid => {
                    const player = event.player_details?.find(p => p.player_id === pid || p.user_id === pid);
                    return player || { player_id: pid, name: 'Unknown Player', type: 'unknown' };
                  });
                  
                  return (
                    <div key={team.team_id} className="border rounded-lg p-4">
                      <h4 className="font-semibold text-[var(--ps-text-primary)] mb-2">{team.name}</h4>
                      <div className="space-y-2">
                        {teamPlayers.map((player, idx) => (
                          <div key={player.player_id || idx} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              {player.picture ? (
                                <img src={player.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <span className="text-green-700 font-medium text-sm">
                                  {player.name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-[var(--ps-text-primary)]">{player.name}</span>
                              {player.type === 'guest' && (
                                <span className="text-xs bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)] px-1.5 py-0.5 rounded">Guest</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {team.player_ids.length < 2 && (
                          <p className="text-xs text-amber-600">Need {2 - team.player_ids.length} more player(s)</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'players' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Players ({event.player_details?.length || 0})</CardTitle>
            {isCreator && (
              <span 
                onClick={() => setShowAddPlayers(true)}
                className="text-sm font-bold text-red-600 hover:text-red-800 cursor-pointer flex items-center gap-1"
              >
                <PlusCircle className="w-4 h-4" />
                Add Players
              </span>
            )}
          </CardHeader>
          <CardContent>
            {(() => {
              // Group players by team and sort teams alphabetically
              const teamGroups = {};
              const unassigned = [];
              
              event.player_details?.forEach((player) => {
                const team = event.teams?.find(t => t.player_ids.includes(player.player_id));
                if (team) {
                  if (!teamGroups[team.name]) {
                    teamGroups[team.name] = { team, players: [] };
                  }
                  teamGroups[team.name].players.push(player);
                } else {
                  unassigned.push(player);
                }
              });
              
              // Sort team names alphabetically
              const sortedTeamNames = Object.keys(teamGroups).sort((a, b) => a.localeCompare(b));
              
              return (
                <div className="space-y-6">
                  {sortedTeamNames.map((teamName) => {
                    const { team, players } = teamGroups[teamName];
                    return (
                      <div key={team.team_id}>
                        <div className="flex items-center gap-2 mb-3">
                          <Users className="w-4 h-4 text-green-600" />
                          <h3 className="font-semibold text-[var(--ps-text-primary)]">{teamName}</h3>
                          <span className="text-xs text-[var(--ps-text-muted)]">({players.length} players)</span>
                        </div>
                        <div className="space-y-2 ml-6">
                          {players.map((player) => (
                            <div key={player.player_id} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                  {player.picture ? (
                                    <img src={player.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                                  ) : (
                                    <span className="text-red-400 font-black">{player.name?.charAt(0).toUpperCase()}</span>
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span 
                                      onClick={() => player.type !== 'guest' && setViewingProfileId(player.player_id)}
                                      className={`font-bold ${player.type !== 'guest' ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:underline' : 'text-[var(--ps-text-primary)]'}`}
                                    >
                                      {player.name}
                                    </span>
                                    {player.type === 'guest' && (
                                      <span className="text-xs bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)] px-1.5 py-0.5 rounded">Guest</span>
                                    )}
                                    {player.is_substitute && (
                                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Sub</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-[var(--ps-text-muted)]">
                                    {player.username ? `@${player.username}` : player.player_id === event.creator_id ? 'Creator' : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex items-center gap-2">
                                {/* Inline handicap editing for creator */}
                                {isCreator && editingHandicapPlayer === player.player_id ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      max="54"
                                      className="w-16 h-8 text-xs"
                                      placeholder="HCP"
                                      value={newHandicapValue}
                                      onChange={(e) => setNewHandicapValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') updatePlayerHandicap(player.player_id, newHandicapValue);
                                        if (e.key === 'Escape') { setEditingHandicapPlayer(null); setNewHandicapValue(''); }
                                      }}
                                      autoFocus
                                    />
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-600" onClick={() => updatePlayerHandicap(player.player_id, newHandicapValue)}>
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[var(--ps-text-muted)]" onClick={() => { setEditingHandicapPlayer(null); setNewHandicapValue(''); }}>
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <div>
                                      <p className="text-sm font-bold text-red-600">
                                        HCP: {handicapLabel(player.handicap)}
                                      </p>
                                      <p className="text-xs text-[var(--ps-text-muted)]">{player.handicap_status}</p>
                                    </div>
                                    {isCreator && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => { 
                                          setEditingHandicapPlayer(player.player_id); 
                                          setNewHandicapValue(player.handicap?.toString() || ''); 
                                        }} 
                                        className="text-blue-500 hover:text-blue-700 h-8 w-8 p-0"
                                        title="Edit handicap"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Unassigned Players */}
                  {unassigned.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <User className="w-4 h-4 text-[var(--ps-text-muted)]" />
                        <h3 className="font-semibold text-[var(--ps-text-muted)]">Unassigned</h3>
                        <span className="text-xs text-[var(--ps-text-muted)]">({unassigned.length} players)</span>
                      </div>
                      <div className="space-y-2 ml-6">
                        {unassigned.map((player) => (
                          <div key={player.player_id} className="flex items-center justify-between p-3 border rounded-lg bg-[var(--ps-bg-elevated)]">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                {player.picture ? (
                                  <img src={player.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                  <span className="text-red-400 font-black">{player.name?.charAt(0).toUpperCase()}</span>
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span 
                                    onClick={() => player.type !== 'guest' && setViewingProfileId(player.player_id)}
                                    className={`font-bold ${player.type !== 'guest' ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:underline' : 'text-[var(--ps-text-primary)]'}`}
                                  >
                                    {player.name}
                                  </span>
                                  {player.type === 'guest' && (
                                    <span className="text-xs bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)] px-1.5 py-0.5 rounded">Guest</span>
                                  )}
                                  {player.is_substitute && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Sub</span>
                                  )}
                                </div>
                                <p className="text-xs text-[var(--ps-text-muted)]">
                                  {player.username ? `@${player.username}` : player.player_id === event.creator_id ? 'Creator' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-2">
                              {/* Inline handicap editing for creator */}
                              {isCreator && editingHandicapPlayer === player.player_id ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="54"
                                    className="w-16 h-8 text-xs"
                                    placeholder="HCP"
                                    value={newHandicapValue}
                                    onChange={(e) => setNewHandicapValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') updatePlayerHandicap(player.player_id, newHandicapValue);
                                      if (e.key === 'Escape') { setEditingHandicapPlayer(null); setNewHandicapValue(''); }
                                    }}
                                    autoFocus
                                  />
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-600" onClick={() => updatePlayerHandicap(player.player_id, newHandicapValue)}>
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[var(--ps-text-muted)]" onClick={() => { setEditingHandicapPlayer(null); setNewHandicapValue(''); }}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <div>
                                    <p className="text-sm font-bold text-red-500">
                                      HCP: {handicapLabel(player.handicap)}
                                    </p>
                                    <span className="text-xs text-amber-600">Not in a team</span>
                                  </div>
                                  {isCreator && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => { 
                                        setEditingHandicapPlayer(player.player_id); 
                                        setNewHandicapValue(player.handicap?.toString() || ''); 
                                      }} 
                                      className="text-blue-500 hover:text-blue-700 h-8 w-8 p-0"
                                      title="Edit handicap"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {activeTab === 'handicaps' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Player Handicaps
            </CardTitle>
            <p className="text-sm text-[var(--ps-text-muted)]">Handicaps are calculated after 3 rounds played</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {handicaps.map((player) => (
                <div key={player.player_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium text-[var(--ps-text-primary)]">{player.name}</p>
                    <p className="text-xs text-[var(--ps-text-muted)]">{player.rounds_played} rounds played</p>
                  </div>
                  <div className="text-right">
                    {player.handicap !== null ? (
                      <p className="text-xl font-bold text-green-700">{player.handicap}</p>
                    ) : (
                      <p className="text-sm text-amber-600">{player.handicap_status}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      {showAddPlayers && (
        <AddPlayersModal eventId={eventId} onClose={() => setShowAddPlayers(false)} onAdded={refreshAll} />
      )}
      {showCreateTeam && (
        <CreateTeamModal eventId={eventId} players={event.player_details || []} teams={event.teams || []} onClose={() => setShowCreateTeam(false)} onCreated={refreshAll} />
      )}
      {showAddScore && (
        <Dialog open={showAddScore} onOpenChange={setShowAddScore}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Score - Coming Soon</DialogTitle>
            </DialogHeader>
            <p className="text-gray-500">Score entry for Match Play rounds will be available soon.</p>
            <DialogFooter>
              <Button onClick={() => setShowAddScore(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {showSetMatchups && (
        <Dialog open={showSetMatchups} onOpenChange={(open) => { setShowSetMatchups(open); if (!open) setEditingWeekMatchups(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Matchups - Coming Soon</DialogTitle>
            </DialogHeader>
            <p className="text-gray-500">Matchup configuration will be available soon.</p>
            <DialogFooter>
              <Button onClick={() => { setShowSetMatchups(false); setEditingWeekMatchups(null); }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {showScheduleManager && (
        <Dialog open={showScheduleManager} onOpenChange={setShowScheduleManager}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule Manager - Coming Soon</DialogTitle>
            </DialogHeader>
            <p className="text-gray-500">Full schedule management will be available soon.</p>
            <DialogFooter>
              <Button onClick={() => setShowScheduleManager(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Substitute Player Modal */}
      {showSubstituteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Substitute Player - Week {scoresWeek}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowSubstituteModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-[var(--ps-text-muted)]">
                Replace <span className="font-medium">{showSubstituteModal.player_name}</span> for this week only
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Info box */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Substitutes play with <strong>0 handicap</strong> for this week.
                </p>
              </div>

              {/* Substitute type toggle */}
              <div className="flex gap-2">
                <Button
                  variant={substituteType === 'existing' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSubstituteType('existing')}
                  className={substituteType === 'existing' ? 'btn-primary' : ''}
                >
                  Select Existing
                </Button>
                <Button
                  variant={substituteType === 'new' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSubstituteType('new')}
                  className={substituteType === 'new' ? 'btn-primary' : ''}
                >
                  Add New Sub
                </Button>
              </div>

              {/* Existing substitute selection */}
              {substituteType === 'existing' && (
                <div>
                  <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">Select Substitute</label>
                  {availableSubstitutes.length === 0 ? (
                    <p className="text-sm text-[var(--ps-text-muted)] py-2">No substitutes available. Add a new one.</p>
                  ) : (
                    <select
                      value={selectedSubstitute}
                      onChange={(e) => setSelectedSubstitute(e.target.value)}
                      className="w-full p-2 border rounded-lg bg-white"
                    >
                      <option value="">-- Select a substitute --</option>
                      {availableSubstitutes.map((sub) => (
                        <option key={sub.player_id} value={sub.player_id}>
                          {sub.name} {sub.is_substitute ? '(Sub - HCP: 0)' : '(Unassigned)'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* New substitute input */}
              {substituteType === 'new' && (
                <div>
                  <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">New Substitute Name</label>
                  <Input
                    placeholder="Enter substitute name..."
                    value={newSubName}
                    onChange={(e) => setNewSubName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubstitutePlayer()}
                  />
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">This will create a new substitute player (HCP: 0)</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowSubstituteModal(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary" 
                  onClick={handleSubstitutePlayer}
                  disabled={addingSubstitute || (substituteType === 'existing' && !selectedSubstitute) || (substituteType === 'new' && !newSubName.trim())}
                >
                  {addingSubstitute ? 'Substituting...' : 'Confirm Substitute'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Score History Modal */}
      {showScoreHistory && scoreHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="flex-shrink-0 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-green-600" />
                  Score History: {scoreHistory.player_name}
                </CardTitle>
                <p className="text-sm text-[var(--ps-text-muted)]">All rounds and edit history</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowScoreHistory(null); setScoreHistory(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {scoreHistory.rounds?.length === 0 ? (
                <p className="text-center text-[var(--ps-text-muted)] py-8">No scores recorded yet.</p>
              ) : (
                <div className="space-y-4">
                  {scoreHistory.rounds?.map((round) => (
                    <div key={round.round_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium">Week {round.week_number}</p>
                          <p className="text-sm text-[var(--ps-text-muted)]">{round.date}</p>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <p className="text-xl font-bold text-green-700">{round.total_score}</p>
                          {round.review_status === 'approved' ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : round.review_status === 'pending' ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">{round.review_status}</span>
                          )}
                        </div>
                      </div>
                      
                      {round.hole_scores?.length === 9 && (
                        <div className="mb-3 p-2 bg-[var(--ps-bg-elevated)] rounded">
                          <p className="text-xs text-[var(--ps-text-muted)] mb-1">Hole Scores:</p>
                          <div className="grid grid-cols-9 gap-1 text-center text-sm">
                            {round.hole_scores.map((score, idx) => (
                              <div key={`item-${idx}`}>
                                <div className="text-xs text-[var(--ps-text-muted)]">{idx + 1}</div>
                                <div className="font-medium">{score}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-[var(--ps-text-muted)] space-y-1">
                        <p>Entry type: <span className="font-medium">{round.score_type}</span></p>
                        <p>Entered by: <span className="font-medium">{round.entered_by_name}</span></p>
                        {round.reviewed_by_name && (
                          <p>Reviewed by: <span className="font-medium">{round.reviewed_by_name}</span></p>
                        )}
                        <p>Created: <span className="font-medium">{new Date(round.created_at).toLocaleString()}</span></p>
                      </div>
                      
                      {/* Edit History */}
                      {round.edit_history?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-[var(--ps-text-secondary)] mb-2">Edit History:</p>
                          <div className="space-y-2">
                            {round.edit_history.map((edit, idx) => (
                              <div key={`event-${idx}`} className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                                <div className="flex justify-between">
                                  <span className="font-medium text-amber-700">{edit.edited_by_name}</span>
                                  <span className="text-[var(--ps-text-muted)]">{new Date(edit.edited_at).toLocaleString()}</span>
                                </div>
                                <p className="text-[var(--ps-text-secondary)] mt-1">
                                  Changed from {edit.old_total_score} to current score
                                </p>
                                {edit.reason && <p className="text-[var(--ps-text-muted)] italic">Reason: {edit.reason}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Manual Points Entry Modal */}
      {showManualPointsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Edit className="w-5 h-5 text-amber-600" />
                    Enter Match Points
                  </CardTitle>
                  <p className="text-sm text-[var(--ps-text-muted)] mt-1">Week {showManualPointsModal.week_number}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowManualPointsModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-medium mb-1">No hole-by-hole scores available</p>
                <p className="text-xs">Enter the match play points manually for this round.</p>
                <p className="text-xs mt-1 text-amber-600">
                  <strong>Hole Points:</strong> Win hole = 1pt, Tie = 0.5pt each (9 pts total per match)
                </p>
              </div>

              {/* Player 1 */}
              <div className="border rounded-lg p-3">
                <p className="font-medium text-sm mb-2">{showManualPointsModal.player1_name}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[var(--ps-text-muted)]">Hole Points (0-9)</label>
                    <Input
                      type="number"
                      min="0"
                      max="9"
                      step="0.5"
                      value={manualPointsData.player1_hole_points}
                      onChange={(e) => setManualPointsData({
                        ...manualPointsData,
                        player1_hole_points: e.target.value,
                        player2_hole_points: (9 - parseFloat(e.target.value || 0)).toString()
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--ps-text-muted)]">Match Points (0/2.5/5)</label>
                    <select
                      value={manualPointsData.player1_match_points}
                      onChange={(e) => setManualPointsData({
                        ...manualPointsData,
                        player1_match_points: e.target.value,
                        player2_match_points: (5 - parseFloat(e.target.value || 0)).toString()
                      })}
                      className="mt-1 w-full border rounded px-3 py-2"
                    >
                      <option value="">Select...</option>
                      <option value="5">5 (Won match)</option>
                      <option value="2.5">2.5 (Tied match)</option>
                      <option value="0">0 (Lost match)</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                  Total: {(parseFloat(manualPointsData.player1_hole_points || 0) + parseFloat(manualPointsData.player1_match_points || 0)).toFixed(1)} pts
                </p>
              </div>

              {/* Player 2 */}
              <div className="border rounded-lg p-3">
                <p className="font-medium text-sm mb-2">{showManualPointsModal.player2_name}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[var(--ps-text-muted)]">Hole Points (0-9)</label>
                    <Input
                      type="number"
                      min="0"
                      max="9"
                      step="0.5"
                      value={manualPointsData.player2_hole_points}
                      onChange={(e) => setManualPointsData({
                        ...manualPointsData,
                        player2_hole_points: e.target.value,
                        player1_hole_points: (9 - parseFloat(e.target.value || 0)).toString()
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--ps-text-muted)]">Match Points (0/2.5/5)</label>
                    <select
                      value={manualPointsData.player2_match_points}
                      onChange={(e) => setManualPointsData({
                        ...manualPointsData,
                        player2_match_points: e.target.value,
                        player1_match_points: (5 - parseFloat(e.target.value || 0)).toString()
                      })}
                      className="mt-1 w-full border rounded px-3 py-2"
                    >
                      <option value="">Select...</option>
                      <option value="5">5 (Won match)</option>
                      <option value="2.5">2.5 (Tied match)</option>
                      <option value="0">0 (Lost match)</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                  Total: {(parseFloat(manualPointsData.player2_hole_points || 0) + parseFloat(manualPointsData.player2_match_points || 0)).toFixed(1)} pts
                </p>
              </div>

              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-sm text-green-800">
                  {showManualPointsModal.player1_name}: <span className="font-bold">{(parseFloat(manualPointsData.player1_hole_points || 0) + parseFloat(manualPointsData.player1_match_points || 0)).toFixed(1)}</span> pts
                  {' vs '}
                  {showManualPointsModal.player2_name}: <span className="font-bold">{(parseFloat(manualPointsData.player2_hole_points || 0) + parseFloat(manualPointsData.player2_match_points || 0)).toFixed(1)}</span> pts
                </p>
                <p className="text-xs text-green-600 mt-1">
                  (Max 14 points per match: 9 hole + 5 match)
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowManualPointsModal(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  className="btn-primary flex-1" 
                  onClick={saveManualPoints}
                  disabled={savingManualPoints || !manualPointsData.player1_match_points}
                >
                  {savingManualPoints ? 'Saving...' : 'Save Points'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Add Players Modal

export default MatchPlayEventDetail;
